import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEventSessions, getAllEvents } from '../services/api';
import { useApiQuery } from '../hooks/useApiQuery';
import {
  ArrowLeftIcon,
  ClockIcon,
  CalendarIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const EventSessionLogs = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const tableRef = useRef(null);
  const [page, setPage] = useState(1);

  // ✅ React Query: Fetch event sessions
  const {
    data: sessions = [],
    isLoading: sessionsLoading,
    isError: sessionsError,
  } = useApiQuery(
    ['event-sessions', eventId],
    () =>
      getEventSessions(eventId).then((res) => {
        const sorted = res.data.sort(
          (a, b) => new Date(a.entryTime) - new Date(b.entryTime),
        );
        return sorted;
      }),
    {
      enabled: !!eventId,
      staleTime: 30 * 1000,
      onError: () => console.error('Error fetching sessions'),
    },
  );

  // ✅ React Query: Fetch all events (for event name)
  const {
    data: allEvents = [],
    isLoading: eventsLoading,
    error: eventsError,
  } = useApiQuery(
    ['all-events'],
    async () => {
      const res = await getAllEvents();
      return Array.isArray(res.data) ? res.data : res.data?.data || [];
    },
    {
      staleTime: 5 * 60 * 1000,
      onError: () => {
        console.error('Failed to load events');
      },
    },
  );

  // ✅ Find event name
  const eventName = useMemo(() => {
    const event = allEvents.find((e) => e._id === eventId);
    return event?.name || '';
  }, [allEvents, eventId]);

  // ✅ Reset to page 1 when event changes
  useEffect(() => {
    setPage(1);
  }, [eventId]);

  // ✅ Client-side pagination
  const pagination = useMemo(() => {
    const total = sessions.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return {
      page,
      limit: PAGE_SIZE,
      total,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  }, [sessions.length, page]);

  // ✅ Sliced sessions for the current page
  const paginatedSessions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sessions.slice(start, start + PAGE_SIZE);
  }, [sessions, page]);

  // ✅ Same smooth-scroll behavior as DeviceSessions
  const goToPage = useCallback(
    (newPage) => {
      const safePage = Math.max(
        1,
        Math.min(newPage, pagination.totalPages || 1),
      );
      setPage(safePage);
      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [pagination.totalPages],
  );

  const isLoading = sessionsLoading || eventsLoading;

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading session logs...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (sessionsError || eventsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 text-lg">Failed to load session logs</p>
          <p className="text-gray-500 text-sm mt-2">
            Please refresh the page or try again later.
          </p>
        </div>
      </div>
    );
  }

  // ✅ Summary stats
  const totalSessions = sessions.length;
  const latestActivity =
    sessions.length > 0 ? sessions[sessions.length - 1].entryTime : null;

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-md text-gray-500 hover:text-[#1672b9] hover:bg-gray-50 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Session Logs – {eventName || 'Event'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Complete check‑in/check‑out history for this event
          </p>
        </div>
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-[#1672b9]" />
            <span className="text-sm text-gray-600">
              Total sessions:{' '}
              <strong className="text-gray-900">{totalSessions}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#1672b9]" />
            <span className="text-sm text-gray-600">
              Latest activity:{' '}
              {latestActivity
                ? new Date(latestActivity).toLocaleDateString()
                : '—'}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-400">
          Sorted by entry time (oldest first)
        </div>
      </div>

      {/* Sessions Table */}
      <div
        ref={tableRef}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-medium text-gray-900 flex items-center gap-2">
            <DevicePhoneMobileIcon className="w-5 h-5 text-gray-400" />
            Session Records
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  No.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedSessions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12">
                    <p className="text-gray-500">
                      No session records found for this event.
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      When attendees check in, their sessions will appear here.
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedSessions.map((session, idx) => (
                  <tr
                    key={session._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 tabular-nums">
                      {(page - 1) * PAGE_SIZE + idx + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {session.customId || session.userId?.customId || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {session.name || session.userId?.name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.email || session.userId?.email || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.entryDevice?.name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.status === 'IN'
                            ? 'bg-[#1672b9]/10 text-[#1672b9]'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {session.status === 'IN' ? 'ENTRY' : 'EXIT'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(session.entryTime).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(session.entryTime).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={!pagination.hasPreviousPage}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventSessionLogs;